// "1u = A$X" — the punter's unit, shared with the Hub so My Bets, the dashboard,
// the bet slip and the leaderboard all speak the same language.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { HUB_BASE, getToken } from '../hub/hubApi';
import './UnitBadge.css';

// Own fetch (not hubApi.api) so a Hub hiccup can never sign someone out of the site.
async function api(path: string, opts: { method?: string; body?: any } = {}) {
  const t = getToken();
  const res = await fetch(HUB_BASE + path, { method: opts.method || 'GET',
    headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: 'Bearer ' + t } : {}) },
    body: opts.body ? JSON.stringify(opts.body) : undefined });
  if (!res.ok) throw new Error('HUB_' + res.status);
  return res.json();
}

type HubUser = { unitValue?: number; leaderboard?: { optIn?: boolean } };
let cache: HubUser | null = null;
const listeners = new Set<(u: HubUser | null) => void>();
let inflight: Promise<HubUser | null> | null = null;

function load(force = false): Promise<HubUser | null> {
  if (cache && !force) return Promise.resolve(cache);
  if (!inflight) inflight = api('/me').then((u: HubUser) => { cache = u; listeners.forEach(f => f(u)); return u; })
    .catch(() => null).finally(() => { inflight = null; });
  return inflight;
}

export function useUnit() {
  const [u, setU] = useState<HubUser | null>(cache);
  useEffect(() => { listeners.add(setU); load(); return () => { listeners.delete(setU); }; }, []);
  const unit = u?.unitValue || 10;
  const save = async (v: number) => {
    const nu = await api('/me', { method: 'PATCH', body: { unitValue: v } });
    cache = nu; listeners.forEach(f => f(nu));
  };
  return { unit, ready: !!u, member: !!u?.leaderboard?.optIn, save };
}

export const toUnits = (dollars: number | null | undefined, unit: number) =>
  dollars == null || !isFinite(dollars) ? null : Math.round(dollars / unit * 100) / 100;
export const fmtUnits = (u: number | null, sign = false) =>
  u == null ? '–' : (sign && u > 0 ? '+' : '') + (Math.abs(u) >= 100 ? u.toFixed(0) : u.toFixed(1)) + 'u';

export const UNIT_LADDER = [0.5, 1, 1.5, 2, 2.5, 3, 4, 5];

const UnitBadge: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const { unit, ready, member, save } = useUnit();
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(String(unit));
  const [err, setErr] = useState('');
  useEffect(() => { setV(String(unit)); }, [unit]);
  if (!ready) return null;
  const submit = async (e: React.FormEvent) => {
    e.preventDefault(); setErr('');
    try { await save(Number(v)); setEdit(false); } catch { setErr('Use a whole number from $1.'); }
  };
  return (
    <div className={'unit-badge' + (compact ? ' compact' : '')}>
      <span className="unit-badge-coin" aria-hidden="true">u</span>
      {!edit ? <>
        <span className="unit-badge-text"><b>1u = A${unit.toFixed(unit % 1 ? 2 : 0)}</b>{!compact && <small>{member ? 'Leaderboard bets: ½u to 5u each' : 'Your unit for every bet'}</small>}</span>
        <button type="button" onClick={() => setEdit(true)}>Change</button>
        {!compact && <Link to="/hub/leaderboard">Leaderboard</Link>}
      </> : <form onSubmit={submit}>
        <label>1u = A$<input type="number" min={1} step={1} value={v} onChange={e => setV(e.target.value)} autoFocus /></label>
        <button type="submit">Save</button><button type="button" className="ghost" onClick={() => setEdit(false)}>Cancel</button>
        {err && <span className="unit-badge-err">{err}</span>}
      </form>}
    </div>
  );
};

export default UnitBadge;
